<template>
  <div
    class="ngc-node-select"
    :class="[
      'ngc-node-select--' + size,
      'ngc-node-select--accent-' + accent,
      { 'ngc-node-select--open': open, 'ngc-node-select--disabled': disabled }
    ]"
    @mousedown.stop
  >
    <button
      type="button"
      class="ngc-node-select__trigger"
      :disabled="disabled"
      :title="displayLabel"
      @click.stop="toggleOpen"
    >
      <span class="ngc-node-select__label">{{ displayLabel }}</span>
      <i
        class="fas fa-chevron-down ngc-node-select__chev"
        :class="{ 'ngc-node-select__chev--up': open }"
        aria-hidden="true"
      ></i>
    </button>
    <transition name="ngc-node-select-pop">
      <div
        v-if="open"
        class="ngc-node-select__menu"
        :class="{ 'ngc-node-select__menu--up': menuUp }"
        role="listbox"
        @mousedown.stop
        @wheel.stop
      >
        <button
          v-for="opt in options"
          :key="optionKey(opt)"
          type="button"
          class="ngc-node-select__option"
          :class="{ 'ngc-node-select__option--active': isSelected(opt) }"
          role="option"
          :aria-selected="isSelected(opt) ? 'true' : 'false'"
          @click.stop="pick(opt)"
        >
          <span class="ngc-node-select__option-text">{{ opt.label }}</span>
          <i
            v-if="isSelected(opt)"
            class="fas fa-check ngc-node-select__option-check"
            aria-hidden="true"
          ></i>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'NgcNodeSelect',
  props: {
    value: {
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: 'sm'
    },
    accent: {
      type: String,
      default: 'default'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    /** 选中后 emit Number（如图片张数） */
    numberValue: {
      type: Boolean,
      default: false
    },
    /** 优先向上展开菜单 */
    menuUp: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: false
    }
  },
  computed: {
    displayLabel() {
      const hit = this.findOption(this.value)
      if (hit) return hit.label
      const raw = this.value
      if (raw !== '' && raw != null) return String(raw)
      return this.placeholder
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        document.addEventListener('mousedown', this.onDocumentMouseDown, true)
        this.$emit('open')
      } else {
        document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
        this.$emit('close')
      }
    }
  },
  mounted() {
    this._ngcCloseSelectHandler = () => {
      this.close()
    }
    if (this.$root) {
      this.$root.$on('ngc-close-node-selects', this._ngcCloseSelectHandler)
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
    if (this.$root && this._ngcCloseSelectHandler) {
      this.$root.$off('ngc-close-node-selects', this._ngcCloseSelectHandler)
    }
  },
  methods: {
    optionKey(opt) {
      return String(opt && opt.id != null ? opt.id : opt && opt.label ? opt.label : '')
    },
    findOption(val) {
      const opts = Array.isArray(this.options) ? this.options : []
      for (let i = 0; i < opts.length; i++) {
        const o = opts[i]
        if (o && String(o.id) === String(val)) return o
      }
      return null
    },
    isSelected(opt) {
      return !!(opt && String(opt.id) === String(this.value))
    },
    toggleOpen() {
      if (this.disabled) return
      this.open = !this.open
    },
    close() {
      this.open = false
    },
    pick(opt) {
      if (!opt) return
      let val = opt.id
      if (this.numberValue) val = Number(val)
      this.$emit('input', val)
      this.close()
    },
    onDocumentMouseDown(e) {
      if (!this.open) return
      const el = this.$el
      if (el && e.target && el.contains(e.target)) return
      this.close()
    }
  }
}
</script>

<style scoped>
.ngc-node-select {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
}
/* 展开时抬高层级，避免节点内后续兄弟块盖住菜单 */
.ngc-node-select--open {
  position: relative;
  z-index: 160;
}
.ngc-node-select--open .ngc-node-select__trigger {
  position: relative;
  z-index: 1;
}
.ngc-node-select__trigger {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  height: 1.75rem;
  padding: 0 0.5rem 0 0.625rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: linear-gradient(180deg, rgba(28, 28, 34, 0.98) 0%, rgba(14, 14, 18, 0.98) 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 2px 8px rgba(0, 0, 0, 0.18);
  font-size: 10px;
  font-weight: 500;
  line-height: 1.25;
  color: rgba(243, 244, 246, 0.95);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease,
    color 0.15s ease;
}
.ngc-node-select__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.ngc-node-select__chev {
  flex-shrink: 0;
  font-size: 8px;
  opacity: 0.65;
  transition: transform 0.18s ease, opacity 0.15s ease;
}
.ngc-node-select__chev--up {
  transform: rotate(180deg);
  opacity: 0.9;
}
.ngc-node-select__trigger:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.ngc-node-select--open .ngc-node-select__trigger {
  border-color: rgba(139, 92, 246, 0.5);
  box-shadow:
    0 0 0 2px rgba(139, 92, 246, 0.16),
    0 1px 0 rgba(255, 255, 255, 0.06) inset;
}
.ngc-node-select--accent-orange.ngc-node-select--open .ngc-node-select__trigger {
  border-color: rgba(251, 146, 60, 0.55);
  box-shadow:
    0 0 0 2px rgba(251, 146, 60, 0.14),
    0 1px 0 rgba(255, 255, 255, 0.06) inset;
}
.ngc-node-select--accent-violet.ngc-node-select--open .ngc-node-select__trigger {
  border-color: rgba(167, 139, 250, 0.55);
  box-shadow:
    0 0 0 2px rgba(139, 92, 246, 0.2),
    0 1px 0 rgba(255, 255, 255, 0.06) inset;
}
.ngc-node-select--accent-indigo.ngc-node-select--open .ngc-node-select__trigger {
  border-color: rgba(129, 140, 248, 0.55);
  box-shadow:
    0 0 0 2px rgba(99, 102, 241, 0.18),
    0 1px 0 rgba(255, 255, 255, 0.06) inset;
}
.ngc-node-select--disabled .ngc-node-select__trigger {
  opacity: 0.45;
  cursor: not-allowed;
}
.ngc-node-select--md .ngc-node-select__trigger {
  height: 2rem;
  font-size: 11px;
  border-radius: 12px;
}
.ngc-node-select--narrow {
  width: 2.85rem;
  min-width: 2.85rem;
}
.ngc-node-select--narrow .ngc-node-select__trigger {
  justify-content: center;
  padding: 0 1.1rem 0 0.35rem;
}
.ngc-node-select--narrow .ngc-node-select__label {
  text-align: center;
}
.ngc-node-select__menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  z-index: 2;
  max-height: min(220px, 42vh);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(20, 20, 26, 0.98);
  box-shadow:
    0 18px 48px -14px rgba(0, 0, 0, 0.85),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  backdrop-filter: blur(16px);
}
.ngc-node-select__menu--up {
  top: auto;
  bottom: calc(100% + 6px);
}
.ngc-node-select__option {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 7px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  font-size: 10px;
  line-height: 1.35;
  color: rgba(229, 231, 235, 0.92);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.ngc-node-select__option-text {
  flex: 1;
  min-width: 0;
}
.ngc-node-select__option:hover {
  background: rgba(139, 92, 246, 0.16);
  color: #fff;
}
.ngc-node-select--accent-orange .ngc-node-select__option:hover {
  background: rgba(251, 146, 60, 0.14);
}
.ngc-node-select--accent-indigo .ngc-node-select__option:hover {
  background: rgba(99, 102, 241, 0.16);
}
.ngc-node-select__option--active {
  background: rgba(139, 92, 246, 0.22);
  color: #fff;
}
.ngc-node-select--accent-orange .ngc-node-select__option--active {
  background: rgba(251, 146, 60, 0.18);
}
.ngc-node-select--accent-violet .ngc-node-select__option--active {
  background: rgba(139, 92, 246, 0.24);
}
.ngc-node-select--accent-indigo .ngc-node-select__option--active {
  background: rgba(99, 102, 241, 0.22);
}
.ngc-node-select__option-check {
  flex-shrink: 0;
  font-size: 9px;
  opacity: 0.9;
}
.ngc-node-select-pop-enter-active,
.ngc-node-select-pop-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}
.ngc-node-select-pop-enter,
.ngc-node-select-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
.ngc-node-select__menu--up.ngc-node-select-pop-enter,
.ngc-node-select__menu--up.ngc-node-select-pop-leave-to {
  transform: translateY(4px) scale(0.98);
}
</style>
